// Node-detailpagina. Blurb, sections (core/deep), practical, walkTour, kinderen,
// beroemde figuren, compendium-doorlinks, links, sources.
// Stubs krijgen "Maak volledige entry"; bestaande entries krijgen "Breid uit via LLM".

import { setTitle, navigate } from '../main.js'
import { md } from '../markdown.js'
import { icon, hasIcon } from '../icons.js'
import { isStub, effectiveSections } from '../data.js'
import { getStore, setGeneratedSections, toggleFavorite } from '../store.js'
import { generateEntry, setState } from '../api.js'
import { fetchImage } from '../images.js'
import { escapeHtml } from '../util.js'

const SECTION_LABELS = {
  beschrijving: 'Beschrijving',
  geschiedenis: 'Geschiedenis',
  weetjes:      'Weetjes',
  waaropletten: 'Waar op te letten',
}

const RELATION_LABELS = {
  born:     'geboren',
  lived:    'woonde',
  worked:   'werkte',
  died:     'gestorven',
  connected:'verbonden',
}

const PRACTICAL_LABELS = {
  openingHours:  'Openingstijden',
  closedDays:    'Gesloten',
  seasonal:      'Seizoen',
  tourLanguages: 'Rondleiding',
  ticket:        'Ticket',
  durationMin:   'Duur (min)',
  parking:       'Parkeren',
}

const DAY_LABELS = { ma: 'ma', di: 'di', wo: 'wo', do: 'do', vr: 'vr', za: 'za', zo: 'zo' }

function renderBadges(node, ctx) {
  const out = []
  const cat = ctx.indexes.categoryById.get(node.category)
  if (cat) {
    const ic = hasIcon(cat.icon) ? icon(cat.icon) : ''
    out.push(`<span class="badge">${ic} ${escapeHtml(cat.label)}</span>`)
  }
  if (node.visit) {
    const tier = ctx.indexes.tierById.get(node.visit)
    out.push(`<span class="badge tier-${node.visit}">${escapeHtml(tier?.label ?? node.visit)}</span>`)
  }
  if (node.stars) {
    out.push(`<span class="badge stars">${icon('star')} ${'★'.repeat(node.stars)}</span>`)
  }
  if (typeof node.driveTimeMin === 'number') {
    out.push(`<span class="badge">${icon('car')} ${node.driveTimeMin} min</span>`)
  }
  for (const eraId of node.eras ?? []) {
    const era = ctx.indexes.eraById.get(eraId)
    if (!era) continue
    out.push(`<span class="badge era" style="background:${era.color}">${escapeHtml(era.label)}</span>`)
  }
  return out.length ? `<div class="badge-row">${out.join('')}</div>` : ''
}

function sectionTitle(s) {
  return s.title || SECTION_LABELS[s.type] || s.type
}

function renderSection(s) {
  const body = `<div class="section-body">${md(s.body)}</div>`
  const tag = s.source === 'generated'
    ? `<span class="badge" title="Door Claude gegenereerd">✨ gegenereerd</span>`
    : ''
  if (s.depth === 'deep') {
    return `<details class="deep"><summary>${escapeHtml(sectionTitle(s))} ${tag}</summary>${body}</details>`
  }
  return `<div class="card"><h2>${escapeHtml(sectionTitle(s))} ${tag}</h2>${body}</div>`
}

function renderPractical(p) {
  if (!p || Object.keys(p).length === 0) return ''
  const rows = []
  for (const [k, label] of Object.entries(PRACTICAL_LABELS)) {
    const v = p[k]
    if (v === undefined || v === null) continue
    let display = v
    if (k === 'closedDays' && Array.isArray(v)) display = v.map(d => DAY_LABELS[d] ?? d).join(', ')
    else if (k === 'tourLanguages' && Array.isArray(v)) display = v.join(', ')
    rows.push(`<div class="k">${label}</div><div class="v">${escapeHtml(String(display))}</div>`)
  }
  if (!rows.length) return ''
  return `<div class="card"><h2>Praktisch</h2><div class="practical">${rows.join('')}</div></div>`
}

function renderLinks(links) {
  if (!links?.length) return ''
  return `
    <div class="card">
      <h2>Links</h2>
      <ul class="link-list">
        ${links.map(l => `<li><a href="${escapeHtml(l.url)}" target="_blank" rel="noopener">
          ${icon('external-link')} ${escapeHtml(l.label)}
        </a></li>`).join('')}
      </ul>
    </div>`
}

function renderSources(srcs) {
  if (!srcs?.length) return ''
  return `
    <div class="card">
      <h2>Bronnen</h2>
      <ul class="link-list">
        ${srcs.map(s => `<li><a href="${escapeHtml(s.url)}" target="_blank" rel="noopener">
          ${icon('external-link')} ${escapeHtml(s.title)}
        </a></li>`).join('')}
      </ul>
    </div>`
}

function renderImageCredit(obj) {
  const img = obj?.imageRef
  if (!img) return ''
  const parts = []
  if (img.credit) parts.push(escapeHtml(img.credit))
  if (img.license) {
    parts.push(img.licenseUrl
      ? `<a href="${escapeHtml(img.licenseUrl)}" target="_blank" rel="noopener">${escapeHtml(img.license)}</a>`
      : escapeHtml(img.license))
  }
  if (img.source) {
    parts.push(`<a href="${escapeHtml(img.source)}" target="_blank" rel="noopener">bron</a>`)
  }
  return parts.length
    ? `<div class="image-credit">${parts.join(' · ')}</div>`
    : ''
}

function renderWalkTour(tour, indexes) {
  if (!tour?.length) return ''
  const items = tour.map(child => {
    const cat = indexes.categoryById.get(child.category)
    return `
      <a class="entity cat-${child.category || 'gebouw'}" href="#/node/${child.id}">
        <div class="icon-bubble">${cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('sparkles')}</div>
        <div class="e-body">
          <div class="e-title">${child.walkTour.order}. ${escapeHtml(child.name)}</div>
          ${child.walkTour.note ? `<div class="e-note">${escapeHtml(child.walkTour.note)}</div>` : ''}
        </div>
      </a>`
  }).join('')
  return `<div class="card"><h2>Wandeling</h2><div class="entity-list">${items}</div></div>`
}

function renderChildren(children, indexes, walkTourIds) {
  const others = children.filter(c => !walkTourIds.has(c.id))
  if (!others.length) return ''
  const items = others.map(child => {
    const cat = indexes.categoryById.get(child.category)
    return `
      <a class="entity cat-${child.category || 'gebouw'}" data-child-id="${escapeHtml(child.id)}" href="#/node/${child.id}">
        <div class="icon-bubble entity-thumb" data-thumb-node="${escapeHtml(child.id)}">
          ${cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('sparkles')}
        </div>
        <div class="e-body">
          <div class="e-title">${escapeHtml(child.name)}</div>
          ${child.blurb ? `<div class="e-note">${escapeHtml(child.blurb)}</div>` : ''}
        </div>
      </a>`
  }).join('')
  return `<div class="card"><h2>Hier in de buurt</h2><div class="entity-list">${items}</div></div>`
}

function renderPeopleHere(items) {
  if (!items?.length) return ''
  const rows = items.map(({ person, relation }) => `
    <a class="entity" href="#/people/${person.id}">
      <div class="icon-bubble entity-thumb" data-thumb-person="${escapeHtml(person.id)}">${icon('users')}</div>
      <div class="e-body">
        <div class="e-title">${escapeHtml(person.name)}</div>
        <div class="e-meta">${person.years ? escapeHtml(person.years) + ' · ' : ''}${RELATION_LABELS[relation] ?? relation}</div>
        ${person.blurb ? `<div class="e-note">${escapeHtml(person.blurb)}</div>` : ''}
      </div>
    </a>`).join('')
  return `<div class="card"><h2>Beroemde figuren hier</h2><div class="entity-list">${rows}</div></div>`
}

function renderCompendiumLinks(items) {
  if (!items?.length) return ''
  const rows = items.map(c => `
    <a class="entity" href="#/encyclopedie/${c.id}">
      <div class="icon-bubble">${icon('book-open')}</div>
      <div class="e-body">
        <div class="e-title">${escapeHtml(c.title)}</div>
        ${c.summary ? `<div class="e-note">${escapeHtml(c.summary.slice(0, 140))}${c.summary.length > 140 ? '…' : ''}</div>` : ''}
      </div>
    </a>`).join('')
  return `<div class="card"><h2>Lees verder in de encyclopedie</h2><div class="entity-list">${rows}</div></div>`
}

function renderRouteCard(node) {
  if (!node.coord) return ''
  const dest = `${node.coord.lat},${node.coord.lng}`
  const driveUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`
  const walkUrl  = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=walking`
  const viewUrl  = `https://www.google.com/maps/search/?api=1&query=${dest}`
  return `
    <div class="card route-card">
      <h2>Navigatie</h2>
      <div class="btn-row">
        <a class="btn" href="#/map?node=${node.id}">${icon('map')} Op de kaart</a>
        <a class="btn secondary" href="${driveUrl}" target="_blank" rel="noopener">${icon('car')} Rijden</a>
        <a class="btn secondary" href="${walkUrl}" target="_blank" rel="noopener">${icon('walk')} Lopen</a>
      </div>
    </div>`
}

function renderStubCallout() {
  return `
    <div class="card" id="stub-callout">
      <h2>Nog geen volledige entry</h2>
      <p>Deze plek heeft alleen een korte introductie. Je kunt Claude een uitgebreide entry laten genereren — beschrijving, geschiedenis en weetjes — gebaseerd op wat we al weten.</p>
      <div class="btn-row">
        <button class="btn" id="generate-btn">${icon('sparkles')} Maak volledige entry</button>
      </div>
      <div id="gen-status" class="gen-loading" style="display:none">
        <div class="spinner"></div>
        <span>Claude is bezig... (dit kan 10-25 seconden duren)</span>
      </div>
    </div>`
}

function renderExpandCallout() {
  return `
    <div class="card" id="expand-callout">
      <div class="btn-row">
        <button class="btn secondary" id="expand-btn">${icon('sparkles')} Breid uit via LLM</button>
      </div>
      <p class="e-meta" style="margin:0">Claude voegt één extra verdiepingsblok toe over een nieuw aspect.</p>
      <div id="expand-status" class="gen-loading" style="display:none">
        <div class="spinner"></div>
        <span>Claude denkt na... (10-25 seconden)</span>
      </div>
    </div>`
}

async function callGenerate(node, mode, existing) {
  const payload = {
    id: node.id, name: node.name, kind: node.kind, category: node.category,
    visit: node.visit, blurb: node.blurb, coord: node.coord, eras: node.eras,
    tags: node.tags, nameAlt: node.nameAlt, parentId: node.parentId,
  }
  return generateEntry(payload, { mode, existingSections: existing })
}

function wireGenerate(root, node) {
  const btn = root.querySelector('#generate-btn')
  const status = root.querySelector('#gen-status')
  btn?.addEventListener('click', async () => {
    btn.disabled = true
    status.style.display = 'flex'
    try {
      const res = await callGenerate(node, 'full', [])
      if (!res?.sections?.length) throw new Error('Lege response van de server.')
      setGeneratedSections(node.id, res.sections)
    } catch (e) {
      console.error(e)
      btn.disabled = false
      status.innerHTML = '<span class="err">Genereren mislukt: ' + e.message + '</span>'
    }
  })
}

function wireFavorite(root, node) {
  const btn = root.querySelector('#node-fav')
  btn?.addEventListener('click', async () => {
    const nowFav = toggleFavorite(node.id)
    btn.classList.toggle('is-fav', nowFav)
    btn.setAttribute('aria-pressed', String(nowFav))
    btn.innerHTML = nowFav ? icon('heart-filled') : icon('heart')
    btn.title = nowFav ? 'Uit favorieten halen' : 'Toevoegen aan favorieten'
    try {
      await setState({ favorites: { [node.id]: nowFav ? true : null } })
    } catch (e) {
      console.warn('favoriet niet opgeslagen', e)
    }
  })
}

function wireExpand(root, node, currentSections) {
  const btn = root.querySelector('#expand-btn')
  const status = root.querySelector('#expand-status')
  btn?.addEventListener('click', async () => {
    btn.disabled = true
    status.style.display = 'flex'
    try {
      // Stuur de bestaande sections mee zodat Claude weet wat er niet herhaald
      // mag worden. Zowel curated als al-eerder-gegenereerde tellen.
      const lean = currentSections.map(s => ({ type: s.type, title: s.title, body: s.body }))
      const res = await callGenerate(node, 'expand', lean)
      if (!res?.sections?.length) throw new Error('Lege response van de server.')
      // Append aan de generated-set
      const store = getStore()
      const existingGenerated = store.generatedSections[node.id] ?? []
      setGeneratedSections(node.id, [...existingGenerated, ...res.sections])
    } catch (e) {
      console.error(e)
      btn.disabled = false
      status.innerHTML = '<span class="err">Uitbreiden mislukt: ' + e.message + '</span>'
    }
  })
}

export function renderNode(root, ctx, nodeId) {
  const node = ctx.indexes.nodesById.get(nodeId)
  if (!node) {
    setTitle('Plek')
    root.innerHTML = `<div class="page"><p class="empty">Onbekende plek: ${escapeHtml(nodeId)}</p></div>`
    return
  }
  setTitle(node.name)

  const parent = node.parentId ? ctx.indexes.nodesById.get(node.parentId) : null
  const children = ctx.indexes.childrenOf.get(node.id) ?? []
  const tour = ctx.indexes.walkTourFor(node.id)
  const tourIds = new Set(tour.map(t => t.id))
  const peopleHere = ctx.indexes.peopleByNode.get(node.id) ?? []
  const compendiumHere = ctx.indexes.compendiumByNode.get(node.id) ?? []
  const guidesHere = ctx.indexes.guidesByNode.get(node.id) ?? []

  const store = getStore()
  const sections = effectiveSections(node, store.generatedSections)
  const stub = isStub(node, store.generatedSections)

  const crumbs = parent
    ? `<div class="crumbs"><a href="#/node/${parent.id}">‹ ${escapeHtml(parent.name)}</a></div>`
    : `<div class="crumbs"><a href="#/map">‹ Kaart</a></div>`

  const guideHint = guidesHere.length
    ? `<p class="subtitle">In ${guidesHere.map(g => `<a href="#/highlights/${g.guide.id}">${escapeHtml(g.guide.title)}</a>`).join(', ')}</p>`
    : ''

  const heroIcon = (() => {
    const cat = ctx.indexes.categoryById.get(node.category)
    return cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('sparkles')
  })()

  const isFav = !!store.game.favorites[node.id]

  root.innerHTML = `
    <div class="page">
      ${crumbs}
      <div class="entity-with-thumb">
        <div class="entity-thumb large" data-thumb-node="${escapeHtml(node.id)}">${heroIcon}</div>
        <div class="hero-body">
          <div class="title-row">
            <h1 class="title">${escapeHtml(node.name)}</h1>
            <button class="fav-toggle large ${isFav ? 'is-fav' : ''}" id="node-fav" aria-pressed="${isFav}" title="${isFav ? 'Uit favorieten halen' : 'Toevoegen aan favorieten'}">
              ${isFav ? icon('heart-filled') : icon('heart')}
            </button>
          </div>
          ${node.nameAlt?.length ? `<p class="subtitle">${node.nameAlt.map(escapeHtml).join(' · ')}</p>` : ''}
          ${guideHint}
          ${renderBadges(node, ctx)}
        </div>
      </div>
      ${renderImageCredit(node)}
      ${node.blurb ? `<div class="blurb">${md(node.blurb)}</div>` : ''}

      ${renderRouteCard(node)}

      ${stub ? renderStubCallout() : ''}

      ${sections.map(renderSection).join('')}

      ${!stub ? renderExpandCallout() : ''}

      ${renderPractical(node.practical)}
      ${renderWalkTour(tour, ctx.indexes)}
      ${renderChildren(children, ctx.indexes, tourIds)}
      ${renderPeopleHere(peopleHere)}
      ${renderCompendiumLinks(compendiumHere)}
      ${renderLinks(node.links)}
      ${renderSources(node.sources)}
    </div>
  `

  if (stub) wireGenerate(root, node)
  else wireExpand(root, node, sections)
  wireFavorite(root, node)

  // Thumbnail-load voor de hero
  fetchImage('node', node, ctx).then(url => {
    if (!url) return
    const slot = root.querySelector(`.entity-thumb.large[data-thumb-node="${CSS.escape(node.id)}"]`)
    if (slot) slot.innerHTML = `<img src="${escapeHtml(url)}" alt="" loading="lazy"/>`
  })

  // Thumbnails voor children-list (lichtere variant)
  for (const child of children) {
    if (tourIds.has(child.id)) continue
    fetchImage('node', child, ctx).then(url => {
      if (!url) return
      const slot = root.querySelector(`.entity-thumb[data-thumb-node="${CSS.escape(child.id)}"]:not(.large)`)
      if (slot) slot.innerHTML = `<img src="${escapeHtml(url)}" alt="" loading="lazy"/>`
    })
  }

  // Thumbnails voor people-rows
  for (const { person } of peopleHere) {
    fetchImage('person', person, ctx).then(url => {
      if (!url) return
      const slot = root.querySelector(`.entity-thumb[data-thumb-person="${CSS.escape(person.id)}"]`)
      if (slot) slot.innerHTML = `<img src="${escapeHtml(url)}" alt="" loading="lazy"/>`
    })
  }
}
